// ═══════════════════════════════════════════════════════════════════════════════
// Today's Todo Page (Helper)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useCallback, useMemo, type ReactNode, type KeyboardEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { IsoDatePicker } from '../../components/IsoDatePicker';
import { StatusFilterChipRow } from '../../components/ui/StatusFilterChipRow';
import { EmployerStatusRibbon } from '../../components/task/employer/EmployerStatusRibbon';
import { taskInstanceApi } from '../../lib/api';
import { buildEmployerGroupedTaskDateHeading } from '../../utils/employerTaskDateHeading';
import {
  addCalendarDaysToYmd,
  formatMonthDayFromYmd,
  getHelperFutureMaxEndYmd,
  getHelperFutureTomorrowYmd,
} from '../../utils/taskEarlySubmit';
import { APPROVAL_STATUS, TASK_KIND, type TodayTodoItem } from '@fdh/shared';
import { resolveDisplayTimeZone } from '../../lib/userTodayYmd';
import { AppPageTitleSection } from '../../components/ui/AppPageTitleSection';
import { HelperEmeraldSegmentTabBar } from '../../components/ui/HelperEmeraldSegmentTabBar';
import { AppButton } from '../../components/ui/AppButton';
import { ListNavRow } from '../../components/ui/ListNavRow';
import { EmployerTaskListTitleIcon } from '../../components/task/employer/EmployerTaskListTitleIcon';
import { ScanReceiptNewspaperIcon } from '../../components/home/ScanReceiptNewspaperIcon';
import {
  AutoTranslationHint,
  resolveTodoItemDisplayDescription,
  resolveTodoItemDisplayNeedsUpdateReason,
  resolveTodoItemDisplayTitle,
  resolveTranslationTargetLanguageLabel,
  todoListHasTranslations,
} from '../../components/translation';

type HelperTab = 'history' | 'overdue' | 'today' | 'future';
type HistoryStatusTab = 'all' | 'not_submitted' | 'pending' | 'approved' | 'needs_update' | 'cancel';

const ISO_YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

const isIsoYmd = (value: string | null): value is string =>
  Boolean(value && ISO_YMD_RE.test(value));

const isHelperTab = (value: string | null): value is HelperTab =>
  value === 'history' || value === 'overdue' || value === 'today' || value === 'future';

const isHistoryStatusTab = (value: string | null): value is HistoryStatusTab =>
  value === 'all' ||
  value === 'not_submitted' ||
  value === 'pending' ||
  value === 'approved' ||
  value === 'needs_update' ||
  value === 'cancel';

const resolveInitialHelperTab = (): HelperTab => {
  if (typeof window === 'undefined') {
    return 'today';
  }
  const tab = new URLSearchParams(window.location.search).get('tab');
  return isHelperTab(tab) ? tab : 'today';
};

const createInitialTabLoading = (initialTab: HelperTab): Record<HelperTab, boolean> => ({
  history: initialTab === 'history',
  overdue: initialTab === 'overdue',
  today: initialTab === 'today',
  future: initialTab === 'future',
});

const createInitialTabFetched = (): Record<HelperTab, boolean> => ({
  history: false,
  overdue: false,
  today: false,
  future: false,
});

const formatDateInTimezone = (date: Date, timezone: string): string => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value ?? '';
  const month = parts.find((part) => part.type === 'month')?.value ?? '';
  const day = parts.find((part) => part.type === 'day')?.value ?? '';
  return `${year}-${month}-${day}`;
};

const parseDueTimeToMinutes = (dueTime: string): number | null => {
  const trimmed = dueTime.trim();
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }
  return hour * 60 + minute;
};

const compareTodoByDueTime = (a: TodayTodoItem, b: TodayTodoItem): number => {
  const ma = a.dueTime ? parseDueTimeToMinutes(a.dueTime) : null;
  const mb = b.dueTime ? parseDueTimeToMinutes(b.dueTime) : null;
  if (ma === null && mb === null) return 0;
  if (ma === null) return 1;
  if (mb === null) return -1;
  return ma - mb;
};

const submittedAtSortKey = (submittedAt: string): string => {
  const trimmed = submittedAt.trim();
  if (trimmed) return trimmed;
  return '0000-00-00 00:00';
};

const extractCompletionDisplay = (submittedAt: string, taskDate: string): string | null => {
  const trimmed = submittedAt.trim();
  if (!trimmed) return null;
  const dateToken = trimmed.includes(' ') ? (trimmed.split(/\s+/)[0] ?? '') : '';
  const timeToken = trimmed.includes(' ') ? (trimmed.split(/\s+/)[1] ?? '') : trimmed;
  const hm = timeToken.slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(hm)) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateToken) && dateToken !== taskDate) {
    return `${dateToken} ${hm}`;
  }
  return hm;
};

const groupHelperTodoByDueDateOldestFirst = (
  tasks: TodayTodoItem[],
): Array<{ date: string; tasks: TodayTodoItem[] }> => {
  const byDate = new Map<string, TodayTodoItem[]>();
  tasks.forEach((task) => {
    const d = task.dueDate;
    const list = byDate.get(d) ?? [];
    list.push(task);
    byDate.set(d, list);
  });
  const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  return dates.map((date) => ({
    date,
    tasks: (byDate.get(date) ?? [])
      .slice()
      .sort((a, b) => {
        const cmp = compareTodoByDueTime(a, b);
        if (cmp !== 0) return cmp;
        return a.title.localeCompare(b.title);
      }),
  }));
};

const groupHelperTodoByDueDateNewestFirst = (
  tasks: TodayTodoItem[],
): Array<{ date: string; tasks: TodayTodoItem[] }> => {
  const byDate = new Map<string, TodayTodoItem[]>();
  tasks.forEach((task) => {
    const d = task.dueDate;
    const list = byDate.get(d) ?? [];
    list.push(task);
    byDate.set(d, list);
  });
  const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  return dates.map((date) => ({
    date,
    tasks: (byDate.get(date) ?? [])
      .slice()
      .sort((a, b) => {
        const cmp = submittedAtSortKey(b.submittedAt).localeCompare(submittedAtSortKey(a.submittedAt));
        if (cmp !== 0) return cmp;
        return a.title.localeCompare(b.title);
      }),
  }));
};

const groupHelperOverdueByDueDateNewestFirst = (
  tasks: TodayTodoItem[],
): Array<{ date: string; tasks: TodayTodoItem[] }> => {
  const byDate = new Map<string, TodayTodoItem[]>();
  tasks.forEach((task) => {
    const d = task.dueDate;
    const list = byDate.get(d) ?? [];
    list.push(task);
    byDate.set(d, list);
  });
  const dates = Array.from(byDate.keys()).sort((a, b) => (a < b ? 1 : a > b ? -1 : 0));
  return dates.map((date) => ({
    date,
    tasks: (byDate.get(date) ?? [])
      .slice()
      .sort((a, b) => {
        const cmp = compareTodoByDueTime(a, b);
        if (cmp !== 0) return cmp;
        return a.title.localeCompare(b.title);
      }),
  }));
};

export const TodayTodoPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialHelperTab = useMemo(() => resolveInitialHelperTab(), []);

  const timezone = useMemo(
    () => resolveDisplayTimeZone(user?.settings.timezone),
    [user?.settings.timezone]
  );
  const todayDate = useMemo(() => formatDateInTimezone(new Date(), timezone), [timezone]);
  const currentMinutes = useMemo(() => {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());
    const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
    const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
    return hour * 60 + minute;
  }, [timezone]);
  const defaultHistoryStart = useMemo(
    () => addCalendarDaysToYmd(todayDate, -6),
    [todayDate],
  );
  const tomorrowYmd = useMemo(() => getHelperFutureTomorrowYmd(todayDate), [todayDate]);
  const defaultFutureEndYmd = useMemo(() => getHelperFutureMaxEndYmd(todayDate), [todayDate]);
  const maxFutureEndYmd = defaultFutureEndYmd;

  const tabQueryValue = searchParams.get('tab');
  const activeTab: HelperTab = isHelperTab(tabQueryValue) ? tabQueryValue : 'today';

  const futStartParam = searchParams.get('futStart');
  const futEndParam = searchParams.get('futEnd');
  const futureStartDate =
    activeTab === 'future'
      ? isIsoYmd(futStartParam)
        ? futStartParam
        : tomorrowYmd
      : tomorrowYmd;
  const futureEndDate =
    activeTab === 'future'
      ? isIsoYmd(futEndParam)
        ? futEndParam
        : defaultFutureEndYmd
      : defaultFutureEndYmd;

  const histStartParam = searchParams.get('histStart');
  const histEndParam = searchParams.get('histEnd');
  const histStatusParam = searchParams.get('histStatus');

  const historyStartDate =
    activeTab === 'history'
      ? isIsoYmd(histStartParam)
        ? histStartParam
        : defaultHistoryStart
      : defaultHistoryStart;
  const historyEndDate =
    activeTab === 'history'
      ? isIsoYmd(histEndParam)
        ? histEndParam
        : todayDate
      : todayDate;
  const historyStatusTab: HistoryStatusTab =
    activeTab === 'history'
      ? isHistoryStatusTab(histStatusParam)
        ? histStatusParam
        : 'all'
      : 'all';

  const [todayTasks, setTodayTasks] = useState<TodayTodoItem[]>([]);
  const [futureTasks, setFutureTasks] = useState<TodayTodoItem[]>([]);
  const [overdueTasks, setOverdueTasks] = useState<TodayTodoItem[]>([]);
  const [historyTasks, setHistoryTasks] = useState<TodayTodoItem[]>([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [tabLoading, setTabLoading] = useState<Record<HelperTab, boolean>>(() =>
    createInitialTabLoading(initialHelperTab),
  );
  const [tabFetched, setTabFetched] = useState<Record<HelperTab, boolean>>(createInitialTabFetched);
  const [error, setError] = useState<string | null>(null);

  const setTabLoadingFor = useCallback((tab: HelperTab, isLoading: boolean) => {
    setTabLoading((prev) => ({ ...prev, [tab]: isLoading }));
  }, []);

  const markTabFetched = useCallback((tab: HelperTab) => {
    setTabFetched((prev) => ({ ...prev, [tab]: true }));
  }, []);

  const isTabContentLoading = tabLoading[activeTab] || !tabFetched[activeTab];
  const [showTaskContentOriginal, setShowTaskContentOriginal] = useState(false);

  useEffect(() => {
    setShowTaskContentOriginal(false);
  }, [language]);

  useEffect(() => {
    setTabLoadingFor(activeTab, true);
  }, [activeTab, setTabLoadingFor]);

  const prefetchFutureTasks = useCallback(async () => {
    if (activeTab === 'future') {
      return;
    }
    try {
      await taskInstanceApi.helperFuture(tomorrowYmd, defaultFutureEndYmd, language);
    } catch {
      // Non-blocking prefetch for lazy translation cache only — do not update futureTasks state
    }
  }, [activeTab, defaultFutureEndYmd, language, tomorrowYmd]);

  const fetchTodayTab = useCallback(async () => {
    setTabLoadingFor('today', true);
    if (activeTab === 'today') {
      setError(null);
    }

    try {
      const [todayResponse, overdueCountRes] = await Promise.all([
        taskInstanceApi.today(language),
        taskInstanceApi.helperOverdueCount(),
      ]);
      if (!todayResponse.success || !todayResponse.data) {
        if (activeTab === 'today') {
          setError(todayResponse.error ?? t('task.today.loadError'));
        }
        return;
      }
      setTodayTasks(todayResponse.data.tasks ?? []);
      setPendingReviewCount(todayResponse.data.pendingReviewCount ?? 0);
      if (overdueCountRes.success && overdueCountRes.data) {
        setOverdueCount(overdueCountRes.data.total ?? 0);
      } else {
        setOverdueCount(0);
      }
    } catch {
      if (activeTab === 'today') {
        setError(t('task.today.loadError'));
      }
    } finally {
      setTabLoadingFor('today', false);
      markTabFetched('today');
    }

    void prefetchFutureTasks();
  }, [activeTab, language, markTabFetched, prefetchFutureTasks, setTabLoadingFor, t]);

  useEffect(() => {
    void fetchTodayTab();
  }, [fetchTodayTab]);

  useEffect(() => {
    if (tabQueryValue !== 'tomorrow') {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'future');
    if (!isIsoYmd(next.get('futStart'))) {
      next.set('futStart', tomorrowYmd);
    }
    if (!isIsoYmd(next.get('futEnd'))) {
      next.set('futEnd', defaultFutureEndYmd);
    }
    setSearchParams(next, { replace: true });
  }, [defaultFutureEndYmd, searchParams, setSearchParams, tabQueryValue, tomorrowYmd]);

  useEffect(() => {
    if (activeTab !== 'future') {
      return;
    }
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!isIsoYmd(next.get('futStart'))) {
      next.set('futStart', tomorrowYmd);
      changed = true;
    }
    if (!isIsoYmd(next.get('futEnd'))) {
      next.set('futEnd', defaultFutureEndYmd);
      changed = true;
    }
    let start = next.get('futStart') ?? tomorrowYmd;
    let end = next.get('futEnd') ?? defaultFutureEndYmd;
    if (!isIsoYmd(start)) start = tomorrowYmd;
    if (!isIsoYmd(end)) end = defaultFutureEndYmd;
    if (start < tomorrowYmd) {
      start = tomorrowYmd;
      next.set('futStart', start);
      changed = true;
    }
    if (end > maxFutureEndYmd) {
      end = maxFutureEndYmd;
      next.set('futEnd', end);
      changed = true;
    }
    if (start > end) {
      next.set('futEnd', start);
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [
    activeTab,
    defaultFutureEndYmd,
    maxFutureEndYmd,
    searchParams,
    setSearchParams,
    tomorrowYmd,
  ]);

  useEffect(() => {
    if (activeTab !== 'history') {
      return;
    }
    const next = new URLSearchParams(searchParams);
    let changed = false;
    if (!isIsoYmd(next.get('histStart'))) {
      next.set('histStart', defaultHistoryStart);
      changed = true;
    }
    if (!isIsoYmd(next.get('histEnd'))) {
      next.set('histEnd', todayDate);
      changed = true;
    }
    if (!isHistoryStatusTab(next.get('histStatus'))) {
      next.set('histStatus', 'all');
      changed = true;
    }
    const hs = next.get('histStart');
    const he = next.get('histEnd');
    if (isIsoYmd(hs) && hs > todayDate) {
      next.set('histStart', todayDate);
      changed = true;
    }
    if (isIsoYmd(he) && he > todayDate) {
      next.set('histEnd', todayDate);
      changed = true;
    }
    const hs2 = next.get('histStart');
    const he2 = next.get('histEnd');
    if (isIsoYmd(hs2) && isIsoYmd(he2) && hs2 > he2) {
      next.set('histEnd', hs2);
      changed = true;
    }
    if (changed) {
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, defaultHistoryStart, searchParams, setSearchParams, todayDate]);

  const handleTabChange = (key: HelperTab) => {
    setTabLoadingFor(key, true);
    const next = new URLSearchParams(searchParams);
    if (key === 'today') {
      next.delete('tab');
      next.delete('histStart');
      next.delete('histEnd');
      next.delete('histStatus');
      next.delete('futStart');
      next.delete('futEnd');
    } else {
      next.set('tab', key);
      if (key === 'future') {
        if (!isIsoYmd(next.get('futStart'))) {
          next.set('futStart', tomorrowYmd);
        }
        if (!isIsoYmd(next.get('futEnd'))) {
          next.set('futEnd', defaultFutureEndYmd);
        }
        next.delete('histStart');
        next.delete('histEnd');
        next.delete('histStatus');
      }
      if (key === 'history') {
        next.delete('futStart');
        next.delete('futEnd');
        if (!isIsoYmd(next.get('histStart'))) {
          next.set('histStart', defaultHistoryStart);
        }
        if (!isIsoYmd(next.get('histEnd'))) {
          next.set('histEnd', todayDate);
        }
        if (!isHistoryStatusTab(next.get('histStatus'))) {
          next.set('histStatus', 'all');
        }
      }
    }
    setSearchParams(next, { replace: true });
  };

  const handleHistoryStartDateChange = (value: string) => {
    setTabLoadingFor('history', true);
    const next = new URLSearchParams(searchParams);
    next.set('histStart', value);
    const endRaw = next.get('histEnd');
    if (isIsoYmd(endRaw) && value > endRaw) {
      next.set('histEnd', value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleHistoryEndDateChange = (value: string) => {
    setTabLoadingFor('history', true);
    const next = new URLSearchParams(searchParams);
    next.set('histEnd', value);
    const startRaw = next.get('histStart');
    if (isIsoYmd(startRaw) && value < startRaw) {
      next.set('histStart', value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleHistoryStatusTab = (nextStatus: HistoryStatusTab): void => {
    setTabLoadingFor('history', true);
    const next = new URLSearchParams(searchParams);
    next.set('histStatus', nextStatus);
    setSearchParams(next, { replace: true });
  };

  const handleFutureStartDateChange = (value: string) => {
    setTabLoadingFor('future', true);
    const next = new URLSearchParams(searchParams);
    next.set('futStart', value);
    const endRaw = next.get('futEnd');
    if (isIsoYmd(endRaw) && value > endRaw) {
      next.set('futEnd', value);
    }
    setSearchParams(next, { replace: true });
  };

  const handleFutureEndDateChange = (value: string) => {
    setTabLoadingFor('future', true);
    const next = new URLSearchParams(searchParams);
    next.set('futEnd', value);
    const startRaw = next.get('futStart');
    if (isIsoYmd(startRaw) && value < startRaw) {
      next.set('futStart', value);
    }
    setSearchParams(next, { replace: true });
  };

  const effectiveFutureStart = useMemo(() => {
    const raw = searchParams.get('futStart');
    if (!isIsoYmd(raw)) return futureStartDate;
    if (raw < tomorrowYmd) return tomorrowYmd;
    return raw;
  }, [futureStartDate, searchParams, tomorrowYmd]);

  const effectiveFutureEnd = useMemo(() => {
    const raw = searchParams.get('futEnd');
    if (!isIsoYmd(raw)) return futureEndDate;
    if (raw > maxFutureEndYmd) return maxFutureEndYmd;
    return raw;
  }, [futureEndDate, maxFutureEndYmd, searchParams]);

  const clampedFutureStart =
    effectiveFutureStart > effectiveFutureEnd ? effectiveFutureEnd : effectiveFutureStart;
  const clampedFutureEnd =
    effectiveFutureStart > effectiveFutureEnd ? effectiveFutureStart : effectiveFutureEnd;

  const fetchFuture = useCallback(async () => {
    if (activeTab !== 'future') return;
    setTabLoadingFor('future', true);
    setError(null);
    try {
      const response = await taskInstanceApi.helperFuture(clampedFutureStart, clampedFutureEnd, language);
      if (!response.success || !response.data) {
        setError(response.error ?? t('task.today.loadError'));
        return;
      }
      setFutureTasks(response.data.tasks ?? []);
    } catch {
      setError(t('task.today.loadError'));
    } finally {
      setTabLoadingFor('future', false);
      markTabFetched('future');
    }
  }, [activeTab, clampedFutureEnd, clampedFutureStart, language, markTabFetched, setTabLoadingFor, t]);

  useEffect(() => {
    void fetchFuture();
  }, [fetchFuture]);

  const fetchOverdue = useCallback(async () => {
    if (activeTab !== 'overdue') {
      return;
    }
    setTabLoadingFor('overdue', true);
    setError(null);
    try {
      const response = await taskInstanceApi.helperOverdue(language);
      if (!response.success || !response.data) {
        setError(response.error ?? t('task.today.loadError'));
        return;
      }
      const tasks = response.data.tasks ?? [];
      setOverdueTasks(tasks);
      setOverdueCount(tasks.length);
    } catch {
      setError(t('task.today.loadError'));
    } finally {
      setTabLoadingFor('overdue', false);
      markTabFetched('overdue');
    }
  }, [activeTab, language, markTabFetched, setTabLoadingFor, t]);

  useEffect(() => {
    void fetchOverdue();
  }, [fetchOverdue]);

  const fetchHistory = useCallback(async () => {
    if (activeTab !== 'history') return;
    setTabLoadingFor('history', true);
    setError(null);
    try {
      const response = await taskInstanceApi.history(
        historyStartDate,
        historyEndDate,
        historyStatusTab,
        language,
      );
      if (!response.success || !response.data) {
        setError(response.error ?? t('task.today.loadError'));
        return;
      }
      setHistoryTasks(response.data.tasks ?? []);
    } catch {
      setError(t('task.today.loadError'));
    } finally {
      setTabLoadingFor('history', false);
      markTabFetched('history');
    }
  }, [activeTab, historyEndDate, historyStartDate, historyStatusTab, language, markTabFetched, setTabLoadingFor, t]);

  useEffect(() => {
    void fetchHistory();
  }, [fetchHistory]);

  const handleCompleteTask = (task: TodayTodoItem) => {
    if (task.taskKind === TASK_KIND.INVENTORY) {
      navigate(`/tasks/inventory/${task.taskInstanceId}`);
      return;
    }
    navigate(`/tasks/complete/${task.taskInstanceId}`);
  };

  const handleViewTask = (taskInstanceId: string) => {
    navigate(`/task-instances/${taskInstanceId}`);
  };

  const handleResubmitTask = (task: TodayTodoItem) => {
    if (task.taskKind === TASK_KIND.INVENTORY) {
      navigate(`/tasks/inventory/${task.taskInstanceId}`);
      return;
    }
    navigate(`/tasks/resubmit/${task.taskInstanceId}`);
  };

  const isAwaitingEmployerReview = (task: TodayTodoItem): boolean =>
    task.status === APPROVAL_STATUS.PENDING &&
    Boolean(task.submittedAt && String(task.submittedAt).trim() !== '');

  const historyTasksGrouped = useMemo(
    () => groupHelperTodoByDueDateNewestFirst(historyTasks),
    [historyTasks],
  );

  const overdueTasksGrouped = useMemo(
    () => groupHelperOverdueByDueDateNewestFirst(overdueTasks),
    [overdueTasks],
  );

  const filteredFutureTasks = useMemo(
    () =>
      futureTasks.filter(
        (task) => task.dueDate >= clampedFutureStart && task.dueDate <= clampedFutureEnd,
      ),
    [clampedFutureEnd, clampedFutureStart, futureTasks],
  );

  const futureTasksGrouped = useMemo(
    () => groupHelperTodoByDueDateOldestFirst(filteredFutureTasks),
    [filteredFutureTasks],
  );

  const todayTaskBuckets = useMemo(() => {
    const visible = todayTasks.filter((task) => task.status !== APPROVAL_STATUS.CANCEL);
    const notSubmitted: TodayTodoItem[] = [];
    const needsUpdate: TodayTodoItem[] = [];
    const completed: TodayTodoItem[] = [];
    for (const task of visible) {
      if (task.status === APPROVAL_STATUS.NEEDS_UPDATE) {
        needsUpdate.push(task);
        continue;
      }
      if (
        task.status === APPROVAL_STATUS.PENDING &&
        (!task.submittedAt || String(task.submittedAt).trim() === '')
      ) {
        notSubmitted.push(task);
        continue;
      }
      if (
        task.status === APPROVAL_STATUS.APPROVED ||
        (task.status === APPROVAL_STATUS.PENDING &&
          Boolean(task.submittedAt && String(task.submittedAt).trim() !== ''))
      ) {
        completed.push(task);
      }
    }
    notSubmitted.sort(compareTodoByDueTime);
    needsUpdate.sort(compareTodoByDueTime);
    completed.sort(compareTodoByDueTime);
    return { notSubmitted, needsUpdate, completed };
  }, [todayTasks]);

  const renderTodayGrouped = (): ReactNode => {
    const parts: ReactNode[] = [];
    const pushBlock = (
      blockKey: string,
      title: string,
      tasks: TodayTodoItem[],
      groupKind: 'not_submitted' | 'needs_update' | 'completed'
    ): void => {
      if (tasks.length === 0) return;
      if (parts.length > 0) {
        parts.push(
          <hr
            key={`hr-${blockKey}`}
            className="my-5 border-0 border-t border-gray-200 dark:border-gray-700"
          />
        );
      }
      parts.push(
        <div key={`head-${blockKey}`} className="mb-3 flex items-baseline justify-between gap-2 px-0.5">
          <h2 className="text-base font-bold tracking-tight text-gray-900 dark:text-gray-100">{title}</h2>
          <span className="shrink-0 text-sm font-medium text-emerald-600 dark:text-emerald-400">
            {t('task.list.summaryCount', { count: tasks.length })}
          </span>
        </div>
      );
      parts.push(
        <ul key={`list-${blockKey}`} className="space-y-3" role="list">
          {tasks.map((task) => (
            <HelperTodayGroupedRow
              key={task.taskInstanceId}
              task={task}
              groupKind={groupKind}
              todayDate={todayDate}
              currentMinutes={currentMinutes}
              isAwaitingEmployerReview={isAwaitingEmployerReview}
              onNavigateComplete={handleCompleteTask}
              onNavigateDetail={handleViewTask}
              onResubmit={handleResubmitTask}
              showTaskContentOriginal={showTaskContentOriginal}
              t={t}
            />
          ))}
        </ul>
      );
    };

    pushBlock('ns', t('task.today.sectionNotSubmitted'), todayTaskBuckets.notSubmitted, 'not_submitted');
    pushBlock('nu', t('task.today.sectionNeedsUpdate'), todayTaskBuckets.needsUpdate, 'needs_update');
    pushBlock('co', t('task.today.sectionCompleted'), todayTaskBuckets.completed, 'completed');
    return parts;
  };

  const renderHelperFutureGrouped = (): ReactNode => (
    <>
      {futureTasksGrouped.map((group) => (
        <section
          key={group.date}
          className="space-y-2"
          aria-labelledby={`helper-future-date-${group.date}`}
        >
          <h2
            id={`helper-future-date-${group.date}`}
            className="flex items-baseline justify-between gap-2 text-sm font-semibold tracking-tight text-gray-600 dark:text-gray-300"
          >
            <span className="min-w-0">
              {buildEmployerGroupedTaskDateHeading({
                dateYmd: group.date,
                todayYmd: todayDate,
                tomorrowYmd,
                timezone,
                language,
                t,
              })}
            </span>
            <span className="shrink-0 font-semibold text-emerald-600 dark:text-emerald-400">
              {t('task.history.groupTotalCount', { count: group.tasks.length })}
            </span>
          </h2>
          <ul className="space-y-3" role="list">
            {group.tasks.map((task) => (
              <HelperFutureTaskRow
                key={task.taskInstanceId}
                task={task}
                onViewDetail={handleViewTask}
                showTaskContentOriginal={showTaskContentOriginal}
                t={t}
              />
            ))}
          </ul>
        </section>
      ))}
    </>
  );

  const renderHelperHistoryOrOverdueGrouped = (
    groups: Array<{ date: string; tasks: TodayTodoItem[] }>,
    section: 'history' | 'overdue',
  ): ReactNode => (
    <>
      {groups.map((group) => (
        <section
          key={group.date}
          className="space-y-2"
          aria-labelledby={`helper-todo-${section}-date-${group.date}`}
        >
          <h2
            id={`helper-todo-${section}-date-${group.date}`}
            className="flex items-baseline justify-between gap-2 text-sm font-semibold tracking-tight text-gray-600 dark:text-gray-300"
          >
            <span className="min-w-0">
              {buildEmployerGroupedTaskDateHeading({
                dateYmd: group.date,
                todayYmd: todayDate,
                tomorrowYmd,
                timezone,
                language,
                t,
              })}
            </span>
            <span className="shrink-0 font-semibold text-emerald-600 dark:text-emerald-400">
              {t('task.history.groupTotalCount', { count: group.tasks.length })}
            </span>
          </h2>
          <ul className="space-y-3" role="list">
            {section === 'history'
              ? group.tasks.map((task) => (
                  <HelperHistoryTaskRow
                    key={task.taskInstanceId}
                    task={task}
                    onViewDetail={handleViewTask}
                    showTaskContentOriginal={showTaskContentOriginal}
                    t={t}
                  />
                ))
              : group.tasks.map((task) => (
                  <HelperOverdueTaskRow
                    key={task.taskInstanceId}
                    task={task}
                    onViewDetail={handleViewTask}
                    showTaskContentOriginal={showTaskContentOriginal}
                    t={t}
                  />
                ))}
          </ul>
        </section>
      ))}
    </>
  );

  const currentTasks = activeTab === 'today'
    ? todayTasks
    : activeTab === 'future'
      ? filteredFutureTasks
      : activeTab === 'overdue'
        ? overdueTasks
        : historyTasks;

  const helperTabs: Array<{ key: HelperTab; label: string }> = [
    { key: 'history', label: t('task.todo.tabHistory') },
    { key: 'overdue', label: t('task.todo.tabOverdue') },
    { key: 'today', label: t('task.todo.tabToday') },
    { key: 'future', label: t('task.todo.tabFuture') },
  ];

  const historyStatusTabs: Array<{ key: HistoryStatusTab; label: string }> = [
    { key: 'all', label: t('task.history.statusAll') },
    { key: 'not_submitted', label: t('task.status.notSubmitted') },
    { key: 'pending', label: t('task.status.pending') },
    { key: 'approved', label: t('task.status.approved') },
    { key: 'needs_update', label: t('task.status.needsUpdate') },
    { key: 'cancel', label: t('task.status.cancel') },
  ];

  const historyStatusFilterOptions = useMemo(
    () => historyStatusTabs.map((tab) => ({ value: tab.key, label: tab.label })),
    [historyStatusTabs]
  );

  const tabHasTranslations = useMemo(() => todoListHasTranslations(currentTasks), [currentTasks]);

  const taskContentTargetLanguageLabel = useMemo(() => {
    const first = currentTasks.find((task) => task.translation?.hasTranslation);
    return resolveTranslationTargetLanguageLabel(
      t,
      first?.translation?.targetLanguage,
      language,
    );
  }, [currentTasks, language, t]);

  const emptyTitle =
    activeTab === 'history'
      ? t('task.history.empty')
      : activeTab === 'today'
        ? t('task.today.empty')
        : activeTab === 'overdue'
          ? t('task.todo.helperEmptyOverdue')
          : t('task.todo.helperEmptyFuture');
  const emptyHint =
    activeTab === 'history'
      ? t('task.history.emptyHint')
      : activeTab === 'today'
        ? t('task.today.emptyHint')
        : activeTab === 'overdue'
          ? t('task.todo.helperEmptyOverdueHint')
          : t('task.todo.helperEmptyFutureHint');

  return (
    <div className="min-h-full bg-[#f7f8fb] px-4 py-6 dark:bg-gray-950">
      <AppPageTitleSection
        title={t('task.today.title')}
        subtitle={t('task.today.subtitle')}
        endDecoration={
          <EmployerTaskListTitleIcon className="pointer-events-none absolute bottom-[5px] right-0 z-0 h-[6.93rem] w-[6.93rem] shrink-0" />
        }
      />

      <Link
        to="/receipts/analyze"
        className="mb-4 flex min-h-11 items-center justify-between gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-left shadow-sm transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:border-blue-800/60 dark:bg-blue-950/40 dark:hover:bg-blue-950/55"
      >
        <span className="flex min-w-0 items-center gap-2 text-sm font-semibold text-blue-900 dark:text-blue-100">
          <ScanReceiptNewspaperIcon className="h-6 w-6 shrink-0" />
          {t('task.todo.receiptScanBanner')}
        </span>
        <svg className="h-5 w-5 shrink-0 text-blue-700 dark:text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>

      <div className="flex items-center gap-2">
        <HelperEmeraldSegmentTabBar
          className="min-w-0 flex-1"
          compact={tabHasTranslations}
          tabs={helperTabs.map((tab) => ({
            key: tab.key,
            label: tab.label,
            badgeCount: tab.key === 'overdue' ? overdueCount : undefined,
          }))}
          activeKey={activeTab}
          onChange={handleTabChange}
          ariaLabel={t('task.today.title')}
        />
        {tabHasTranslations ? (
          <AutoTranslationHint
            targetLanguageLabel={taskContentTargetLanguageLabel}
            showingOriginal={showTaskContentOriginal}
            onToggleOriginal={() => setShowTaskContentOriginal((prev) => !prev)}
          />
        ) : null}
      </div>

      {activeTab === 'today' && pendingReviewCount > 0 ? (
        <div className="pt-4">
          <div className="flex items-center gap-2 rounded-xl bg-yellow-50 px-4 py-3 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-100">
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <span className="text-sm font-medium">
              {t('task.today.pendingReview', { count: pendingReviewCount })}
            </span>
          </div>
        </div>
      ) : null}

      {activeTab === 'future' && filteredFutureTasks.length > 0 ? (
        <div className="pt-4">
          <div
            className="flex items-center gap-2 rounded-xl bg-yellow-50 px-4 py-3 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-100"
            role="status"
            aria-live="polite"
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <span className="text-sm font-medium">{t('task.todo.futureSubmitBanner')}</span>
          </div>
        </div>
      ) : null}

      {activeTab === 'future' ? (
        <div className="pt-4">
          <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-gray-900">
            <div className="flex flex-wrap items-center gap-2">
              <IsoDatePicker
                id="helper-task-future-start-date"
                ariaLabel={t('task.history.startDate')}
                value={clampedFutureStart}
                onChange={handleFutureStartDateChange}
                minDate={tomorrowYmd}
                maxDate={clampedFutureEnd || maxFutureEndYmd}
                className="min-w-0 flex-1 basis-[140px]"
                valueFormat="iso"
              />
              <span className="shrink-0 px-0.5 text-sm font-medium text-gray-500 dark:text-gray-400" aria-hidden>
                {t('settlement.helperConfirmDateRangeSeparator')}
              </span>
              <IsoDatePicker
                id="helper-task-future-end-date"
                ariaLabel={t('task.history.endDate')}
                value={clampedFutureEnd}
                onChange={handleFutureEndDateChange}
                minDate={clampedFutureStart || tomorrowYmd}
                maxDate={maxFutureEndYmd}
                className="min-w-0 flex-1 basis-[140px]"
                valueFormat="iso"
              />
            </div>
          </div>
        </div>
      ) : null}

      {activeTab === 'history' ? (
        <div className="space-y-3 pt-4">
          <div className="rounded-xl bg-white p-3 shadow-sm dark:bg-gray-900">
            <div className="flex flex-wrap items-center gap-2">
              <IsoDatePicker
                id="task-history-start-date"
                ariaLabel={t('task.history.startDate')}
                value={historyStartDate}
                onChange={handleHistoryStartDateChange}
                maxDate={historyEndDate || todayDate}
                className="min-w-0 flex-1 basis-[140px]"
                valueFormat="iso"
              />
              <span className="shrink-0 px-0.5 text-sm font-medium text-gray-500 dark:text-gray-400" aria-hidden>
                {t('settlement.helperConfirmDateRangeSeparator')}
              </span>
              <IsoDatePicker
                id="task-history-end-date"
                ariaLabel={t('task.history.endDate')}
                value={historyEndDate}
                onChange={handleHistoryEndDateChange}
                minDate={historyStartDate || undefined}
                maxDate={todayDate}
                className="min-w-0 flex-1 basis-[140px]"
                valueFormat="iso"
              />
            </div>
          </div>
          <StatusFilterChipRow
            ariaLabel={t('task.history.filterAria')}
            options={historyStatusFilterOptions}
            value={historyStatusTab}
            onChange={handleHistoryStatusTab}
          />
        </div>
      ) : null}

      <main className="pt-4">
        {isTabContentLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
          </div>
        ) : error ? (
          <div className="rounded-xl bg-red-50 p-6 text-center">
            <p className="text-red-600" role="alert">{error}</p>
            <button
              type="button"
              onClick={() => {
                if (activeTab === 'history') {
                  void fetchHistory();
                  return;
                }
                if (activeTab === 'future') {
                  void fetchFuture();
                  return;
                }
                if (activeTab === 'overdue') {
                  void fetchOverdue();
                  return;
                }
                void fetchTodayTab();
              }}
              className="mt-3 text-sm font-medium text-red-700 underline hover:no-underline focus:outline-none"
            >
              {t('task.action.refresh')}
            </button>
          </div>
        ) : currentTasks.length === 0 ? (
          <div className="rounded-xl bg-white p-8 text-center shadow-sm">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-gray-600">{emptyTitle}</p>
            <p className="mt-1 text-sm text-gray-400">{emptyHint}</p>
          </div>
        ) : activeTab === 'today' ? (
          <section className="space-y-0">{renderTodayGrouped()}</section>
        ) : activeTab === 'history' ? (
          <section className="space-y-6">{renderHelperHistoryOrOverdueGrouped(historyTasksGrouped, 'history')}</section>
        ) : activeTab === 'overdue' ? (
          <section className="space-y-6">{renderHelperHistoryOrOverdueGrouped(overdueTasksGrouped, 'overdue')}</section>
        ) : activeTab === 'future' ? (
          <section className="space-y-6">{renderHelperFutureGrouped()}</section>
        ) : null}
      </main>
    </div>
  );
};

type HelperTodayGroupedRowProps = {
  task: TodayTodoItem;
  groupKind: 'not_submitted' | 'needs_update' | 'completed';
  todayDate: string;
  currentMinutes: number;
  isAwaitingEmployerReview: (task: TodayTodoItem) => boolean;
  onNavigateComplete: (task: TodayTodoItem) => void;
  onNavigateDetail: (taskInstanceId: string) => void;
  onResubmit: (task: TodayTodoItem) => void;
  showTaskContentOriginal: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const HelperTodayGroupedRow = ({
  task,
  groupKind,
  todayDate,
  currentMinutes,
  isAwaitingEmployerReview,
  onNavigateComplete,
  onNavigateDetail,
  onResubmit,
  showTaskContentOriginal,
  t,
}: HelperTodayGroupedRowProps) => {
  const displayTitle = resolveTodoItemDisplayTitle(task, showTaskContentOriginal);
  const displayDescription = resolveTodoItemDisplayDescription(task, showTaskContentOriginal);
  const displayNeedsUpdateReason = resolveTodoItemDisplayNeedsUpdateReason(
    task,
    showTaskContentOriginal,
  );
  const dueMinutes = task.dueTime ? parseDueTimeToMinutes(task.dueTime) : null;
  const isOverdueTime =
    groupKind === 'not_submitted' &&
    task.status === APPROVAL_STATUS.PENDING &&
    (!task.submittedAt || String(task.submittedAt).trim() === '') &&
    task.dueDate === todayDate &&
    dueMinutes !== null &&
    dueMinutes < currentMinutes;

  const awaitingReviewInGroup =
    groupKind === 'completed' &&
    task.status === APPROVAL_STATUS.PENDING &&
    isAwaitingEmployerReview(task);

  const useGrayDueTimeBox =
    groupKind === 'completed' && (task.status === APPROVAL_STATUS.APPROVED || awaitingReviewInGroup);

  const useEmeraldNeedsUpdateBox = groupKind === 'needs_update';

  const deadlineFallbackText = t('task.todo.deadlineToday');
  const deadlineDisplayText = task.dueTime || deadlineFallbackText;

  const showChevron = groupKind === 'not_submitted' || groupKind === 'completed';
  const showCompletedStatusRibbon = groupKind === 'completed';
  const hasDescription = Boolean(displayDescription && String(displayDescription).trim());
  const hasNeedsUpdateReason =
    groupKind === 'needs_update' &&
    Boolean(displayNeedsUpdateReason && String(displayNeedsUpdateReason).trim());
  const hasSecondaryContent = hasDescription || hasNeedsUpdateReason;

  const timeBoxClass = useEmeraldNeedsUpdateBox
    ? 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100'
    : isOverdueTime
      ? 'bg-red-600 text-white dark:bg-red-700'
      : useGrayDueTimeBox
        ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
        : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100';

  const handleRowActivate = (): void => {
    if (groupKind === 'needs_update') return;
    if (groupKind === 'not_submitted') {
      if (isAwaitingEmployerReview(task)) {
        onNavigateDetail(task.taskInstanceId);
        return;
      }
      onNavigateComplete(task);
      return;
    }
    onNavigateDetail(task.taskInstanceId);
  };

  const handleRowKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (groupKind === 'needs_update') return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRowActivate();
    }
  };

  const rowRoleProps =
    groupKind === 'needs_update'
      ? {}
      : {
          role: 'button' as const,
          tabIndex: 0,
          onClick: handleRowActivate,
          onKeyDown: handleRowKeyDown,
        };

  return (
    <li className="overflow-hidden rounded-2xl border border-gray-200/90 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-900">
      <div
        {...rowRoleProps}
        className={
          groupKind === 'needs_update'
            ? `flex w-full gap-3 p-4 text-left${hasSecondaryContent ? '' : ' items-center'}`
            : `relative flex w-full overflow-hidden cursor-pointer gap-3 p-4 text-left transition-colors hover:bg-gray-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-gray-800/40${hasSecondaryContent ? '' : ' items-center'}`
        }
        aria-label={
          groupKind === 'needs_update'
            ? undefined
            : `${
                task.taskKind === TASK_KIND.INVENTORY
                  ? `${displayTitle} (${t('task.list.kindStockTake')})`
                  : displayTitle
              } — ${t('task.today.view')}`
        }
      >
        {showCompletedStatusRibbon ? (
          <EmployerStatusRibbon status={task.status} submittedAt={task.submittedAt ?? ''} />
        ) : null}
        <div
          className={`flex h-[42px] w-[80px] shrink-0 self-start flex-col items-center justify-center rounded-xl px-2 py-2 ${timeBoxClass}`}
        >
          <p
            className={`whitespace-nowrap font-bold leading-none tracking-tight ${
              task.dueTime ? 'text-[26px] tabular-nums' : 'text-xl'
            }`}
          >
            {deadlineDisplayText}
          </p>
        </div>
        <div className={`min-w-0 flex-1${showCompletedStatusRibbon ? ' pr-10' : ''}`}>
          <div
            className={`flex justify-between gap-2 ${hasSecondaryContent ? 'items-start' : 'items-center'}`}
          >
            <div className="min-w-0 flex-1">
              <h3 className="line-clamp-2 min-w-0 font-semibold text-gray-900 dark:text-gray-100">
                {displayTitle}
                {task.taskKind === TASK_KIND.INVENTORY ? (
                  <>
                    {' '}
                    <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                      {t('task.list.kindStockTake')}
                    </span>
                  </>
                ) : null}
              </h3>
            </div>
          </div>
          {hasDescription ? (
            <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">{displayDescription}</p>
          ) : null}
          {hasNeedsUpdateReason ? (
            <p className="mt-2 text-sm font-medium text-red-600 dark:text-red-400">
              <span className="font-semibold">{t('task.today.employerFeedbackLabel')}</span>{' '}
              {displayNeedsUpdateReason}
            </p>
          ) : null}
        </div>
        {showChevron ? (
          groupKind === 'completed' ? (
            <span
              className="inline-flex w-6 shrink-0 translate-y-2 items-center justify-center self-center text-gray-400 dark:text-gray-500"
              aria-hidden
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </span>
          ) : (
            <svg
              className="h-6 w-6 shrink-0 self-center text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          )
        ) : (
          <span className="h-6 w-6 shrink-0 self-center" aria-hidden />
        )}
      </div>
      {groupKind === 'needs_update' ? (
        <div className="border-t border-gray-100 px-4 pb-4 pt-3 dark:border-gray-800">
          <AppButton
            type="button"
            variant="primary"
            fullWidth
            className="shadow-sm"
            onClick={() => onResubmit(task)}
            aria-label={t('task.resubmit.submit')}
          >
            <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            {t('task.resubmit.submit')}
          </AppButton>
        </div>
      ) : null}
    </li>
  );
};

type HelperHistoryTaskRowProps = {
  task: TodayTodoItem;
  onViewDetail: (taskInstanceId: string) => void;
  showTaskContentOriginal: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const HelperHistoryStatusIcon = ({
  status,
  isAwaitingSubmission,
}: {
  status: TodayTodoItem['status'];
  isAwaitingSubmission: boolean;
}) => {
  if (isAwaitingSubmission) {
    return (
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
        aria-hidden
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
      </div>
    );
  }
  if (status === APPROVAL_STATUS.APPROVED) {
    return (
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950/55 dark:text-emerald-400"
        aria-hidden
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
    );
  }
  if (status === APPROVAL_STATUS.PENDING) {
    return (
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950/45 dark:text-amber-300"
        aria-hidden
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
    );
  }
  if (status === APPROVAL_STATUS.NEEDS_UPDATE) {
    return (
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-600 dark:bg-orange-950/50 dark:text-orange-300"
        aria-hidden
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      </div>
    );
  }
  if (status === APPROVAL_STATUS.CANCEL) {
    return (
      <div
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
        aria-hidden
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
          />
        </svg>
      </div>
    );
  }
  return (
    <div
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-950/45 dark:text-red-300"
      aria-hidden
    >
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
      </svg>
    </div>
  );
};

const HelperHistoryTaskRow = ({
  task,
  onViewDetail,
  showTaskContentOriginal,
  t,
}: HelperHistoryTaskRowProps) => {
  const displayTitle = resolveTodoItemDisplayTitle(task, showTaskContentOriginal);
  const isAwaitingSubmission =
    task.status === APPROVAL_STATUS.PENDING &&
    (!task.submittedAt || String(task.submittedAt).trim() === '');
  const completionDisplay = extractCompletionDisplay(task.submittedAt ?? '', task.dueDate);

  return (
    <li>
      <ListNavRow
        onClick={() => onViewDetail(task.taskInstanceId)}
        ariaLabel={
          task.taskKind === TASK_KIND.INVENTORY
            ? `${displayTitle} (${t('task.list.kindStockTake')})`
            : displayTitle
        }
        ribbon={<EmployerStatusRibbon status={task.status} submittedAt={task.submittedAt ?? ''} />}
        className="pr-1"
      >
        <div className="flex items-center gap-3">
          <HelperHistoryStatusIcon status={task.status} isAwaitingSubmission={isAwaitingSubmission} />
          <div className="min-w-0 flex-1 pr-10">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">{displayTitle}</h3>
              {task.taskKind === TASK_KIND.INVENTORY ? (
                <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                  {t('task.list.kindStockTake')}
                </span>
              ) : null}
            </div>
            {completionDisplay ? (
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('task.history.completionTime', { time: completionDisplay })}
              </p>
            ) : null}
          </div>
        </div>
      </ListNavRow>
    </li>
  );
};

type HelperOverdueTaskRowProps = {
  task: TodayTodoItem;
  onViewDetail: (taskInstanceId: string) => void;
  showTaskContentOriginal: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const HelperOverdueTaskRow = ({
  task,
  onViewDetail,
  showTaskContentOriginal,
  t,
}: HelperOverdueTaskRowProps) => {
  const displayTitle = resolveTodoItemDisplayTitle(task, showTaskContentOriginal);
  const displayDescription = resolveTodoItemDisplayDescription(task, showTaskContentOriginal);
  const deadlineDisplayText = task.dueTime || task.dueDate.slice(5, 10);
  const hasDescription = Boolean(displayDescription && String(displayDescription).trim());

  const titleForAria =
    task.taskKind === TASK_KIND.INVENTORY
      ? `${displayTitle} (${t('task.list.kindStockTake')})`
      : displayTitle;

  const handleRowClick = () => {
    onViewDetail(task.taskInstanceId);
  };

  return (
    <li>
      <ListNavRow
        onClick={handleRowClick}
        ariaLabel={`${titleForAria} — ${t('task.today.view')}`}
        align={hasDescription ? 'start' : 'center'}
        comfortable
        ribbon={<EmployerStatusRibbon status={task.status} submittedAt={task.submittedAt ?? ''} />}
        leading={
          <div className="flex h-[42px] w-[80px] flex-col items-center justify-center rounded-xl bg-red-600 px-2 py-2 text-white dark:bg-red-700">
            <p
              className={`whitespace-nowrap font-bold leading-none tracking-tight ${
                task.dueTime ? 'text-[26px] tabular-nums' : 'text-xl'
              }`}
            >
              {deadlineDisplayText}
            </p>
          </div>
        }
      >
        <div className="min-w-0 pr-10">
          <h3 className="line-clamp-2 min-w-0 font-semibold text-gray-900 dark:text-gray-100">
            {displayTitle}
            {task.taskKind === TASK_KIND.INVENTORY ? (
              <>
                {' '}
                <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                  {t('task.list.kindStockTake')}
                </span>
              </>
            ) : null}
          </h3>
          {hasDescription ? (
            <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">{displayDescription}</p>
          ) : null}
        </div>
      </ListNavRow>
    </li>
  );
};

type HelperFutureTaskRowProps = {
  task: TodayTodoItem;
  onViewDetail: (taskInstanceId: string) => void;
  showTaskContentOriginal: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
};

const HelperFutureTaskRow = ({
  task,
  onViewDetail,
  showTaskContentOriginal,
  t,
}: HelperFutureTaskRowProps) => {
  const displayTitle = resolveTodoItemDisplayTitle(task, showTaskContentOriginal);
  const displayDescription = resolveTodoItemDisplayDescription(task, showTaskContentOriginal);
  const isAwaitingSubmission =
    task.status === APPROVAL_STATUS.PENDING &&
    (!task.submittedAt || String(task.submittedAt).trim() === '');
  const useGrayDueTimeBox =
    task.status === APPROVAL_STATUS.APPROVED ||
    (task.status === APPROVAL_STATUS.PENDING && !isAwaitingSubmission);
  const deadlineDisplayText = task.dueTime
    ? task.dueTime
    : formatMonthDayFromYmd(task.dueDate);
  const hasDescription = Boolean(displayDescription && String(displayDescription).trim());

  const titleForAria =
    task.taskKind === TASK_KIND.INVENTORY
      ? `${displayTitle} (${t('task.list.kindStockTake')})`
      : displayTitle;

  const handleRowClick = () => {
    onViewDetail(task.taskInstanceId);
  };

  return (
    <li>
      <ListNavRow
        onClick={handleRowClick}
        ariaLabel={`${titleForAria} — ${t('task.today.view')}`}
        align={hasDescription ? 'start' : 'center'}
        comfortable
        ribbon={<EmployerStatusRibbon status={task.status} submittedAt={task.submittedAt ?? ''} />}
        leading={
          <div
            className={`flex h-[42px] w-[80px] flex-col items-center justify-center rounded-xl px-2 py-2 ${
              useGrayDueTimeBox
                ? 'bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-100'
                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100'
            }`}
          >
            <p
              className={`whitespace-nowrap font-bold leading-none tracking-tight ${
                task.dueTime ? 'text-[26px] tabular-nums' : 'text-xl'
              }`}
            >
              {deadlineDisplayText}
            </p>
          </div>
        }
      >
        <div className="min-w-0 pr-10">
          <h3 className="line-clamp-2 min-w-0 font-semibold text-gray-900 dark:text-gray-100">
            {displayTitle}
            {task.taskKind === TASK_KIND.INVENTORY ? (
              <>
                {' '}
                <span className="inline-flex shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200">
                  {t('task.list.kindStockTake')}
                </span>
              </>
            ) : null}
          </h3>
          {hasDescription ? (
            <p className="mt-1 line-clamp-2 text-sm text-gray-500 dark:text-gray-400">{displayDescription}</p>
          ) : null}
        </div>
      </ListNavRow>
    </li>
  );
};
